// apps/share-web/src/player-view/setlist-songs-rail.ts
//
// Рельса плеера, построенная из `songs[]` (единый сетлист, фаза 3, design gate
// G2) — младшая сестра `setlist-rail.ts` (та строится из `items[]`, где у
// КАЖДОЙ строки уже есть shareId). Здесь строк может быть больше: сетлист
// несёт ВСЕ песни, включая те, у которых ещё нет записи, — и рельса обязана
// показать их тоже (иначе список «теряет» песни, которые лидер туда положил),
// пометив тихой мета-строкой «Words» вместо пустоты. Выбор такой строки не
// ведёт в плеер — ведёт на поверхность «только слова» (words-only-mount.ts);
// внутри самого этого модуля никакой навигации нет, он только называет, какая
// строка что такое (`hasRecording`), решение — у вызывающего.
//
// Визуал — байт-в-байт рельса `setlist-rail.ts`: тот же `.sl-panel/.sl-header/
// .sl-list`, та же `bw-setlist-card`. Отличие ровно одно — источник данных и
// мета-строка «Words» у строк без записи, поэтому карточка получила
// `metaText` (packages/ui-components/src/bw-setlist-card.ts, design gate G2):
// она включается только когда key/bpm/duration все отсутствуют, то есть
// ничего в существующих рельсах не меняет.
import '@bandway/ui-components';
import '@bandway/ui-components/styles/bw-setlist-card.css';
import '@bandway/ui-components/styles/bw-tag.css';
import { t } from '../ui/i18n.js';

export interface SetlistSongsRailRow {
  /** shareId версии, открытой у этой песни, — null, когда записи нет вовсе. */
  shareId: string | null;
  songName: string;
  key?: string;
  bpm?: number;
  durationSec?: number;
}

export interface SetlistSongsRailDeps {
  rows: SetlistSongsRailRow[];
  /** shareId ТЕКУЩЕЙ песни в плеере — подсвечивает её NOW-чипом, как и обычная рельса. */
  activeShareId: string | null;
  /**
   * Индекс текущей строки в `rows` — единственный способ подсветить песню БЕЗ
   * записи (у неё `shareId === null`, сравнивать нечего). Критика живой
   * приёмки 16073e7c, пункт 2: «highlight the current song exactly as the
   * existing rail does» — для строки без записи `activeShareId` этого сделать
   * не может в принципе. Оба признака проверяются одновременно: строка «now»,
   * если совпал shareId ИЛИ совпал индекс, — так вызывающему не нужно решать,
   * какой из двух признаков подходит для текущей строки, он передаёт оба, что
   * знает.
   */
  activeRowIndex?: number | null;
  name?: string | null;
  createdAt?: string | null;
  /** Песня С записью — ведёт в плеер по её shareId. */
  onSelect: (shareId: string) => void;
  /** Песня БЕЗ записи — ведёт на поверхность «только слова» (индекс строки в `rows`). */
  onSelectWordsOnly: (rowIndex: number) => void;
}

export interface SetlistSongsRailHandle {
  unmount(): void;
}

/** Тот же формат заголовка, что у обычной рельсы: «<день> <мес> · <счёт>». */
function formatMeta(createdAt: string | null | undefined, count: number): string {
  const parts: string[] = [];
  if (createdAt) {
    const d = new Date(createdAt);
    if (!Number.isNaN(d.getTime())) {
      const mon = d.toLocaleString('en', { month: 'short' });
      parts.push(`${d.getDate()} ${mon}`);
    }
  }
  parts.push(String(count));
  return parts.join(' · ');
}

export function mountSetlistSongsRail(host: Element, deps: SetlistSongsRailDeps): SetlistSongsRailHandle {
  host.textContent = '';

  const panel = document.createElement('div');
  panel.className = 'sl-panel';
  panel.dataset.role = 'setlist-songs-rail';

  const header = document.createElement('header');
  header.className = 'sl-header';
  const label = document.createElement('div');
  label.className = 'sl-header-label';
  label.textContent = t('setlist.railHeading');
  header.appendChild(label);
  const name = document.createElement('div');
  name.className = 'sl-header-name';
  name.textContent = deps.name && deps.name.trim() ? deps.name : t('setlist.untitled');
  header.appendChild(name);
  const meta = document.createElement('div');
  meta.className = 'sl-header-meta';
  meta.textContent = formatMeta(deps.createdAt, deps.rows.length);
  header.appendChild(meta);
  panel.appendChild(header);

  const list = document.createElement('div');
  list.className = 'sl-list';
  list.setAttribute('role', 'list');

  type Card = HTMLElement & {
    position: number;
    song: { name: string; key?: string; bpm?: number; durationSec?: number; metaText?: string } | null;
    status: 'idle' | 'now' | 'next';
    mode: string;
  };

  deps.rows.forEach((row, i) => {
    const hasRecording = row.shareId !== null;
    const card = document.createElement('bw-setlist-card') as Card;
    card.dataset.role = 'rail-song';
    card.dataset.hasRecording = String(hasRecording);
    if (row.shareId) card.dataset.shareId = row.shareId;
    card.position = i + 1;
    card.song = {
      name: row.songName || t('setlist.untitled'),
      key: row.key,
      bpm: row.bpm,
      durationSec: row.durationSec,
      // Только когда песня ЛИШЕНА собственной меты (нет записи → нет key/bpm/
      // duration в этом vm) — карточка молча предпочитает вычисленную мету,
      // как только она появится у следующей версии этой песни.
      metaText: hasRecording ? undefined : t('library.wordsTag'),
    };
    card.mode = 'rehearsal';
    const isNow = (hasRecording && row.shareId === deps.activeShareId) || deps.activeRowIndex === i;
    card.status = isNow ? 'now' : 'idle';

    card.addEventListener('bw-setlist-card-select', () => {
      if (card.status === 'now') return;
      if (hasRecording) deps.onSelect(row.shareId as string);
      else deps.onSelectWordsOnly(i);
    });

    list.appendChild(card);
  });
  panel.appendChild(list);

  host.appendChild(panel);

  return {
    unmount(): void { host.textContent = ''; },
  };
}
